import { useCallback, useMemo, useState } from "react";
import type { Status } from "types/Status";

type DownloadStatus = Status | null;

export interface UseFileDownloaderReturn {
  status: DownloadStatus;
  error: unknown | null;
  download: (filename: string, fetcher: () => Promise<Blob>) => Promise<void>;
}

function useFileDownloader(): UseFileDownloaderReturn {
  const [status, setStatus] = useState<DownloadStatus>(null);
  const [error, setError] = useState<unknown | null>(null);

  const download = useCallback(async (filename: string, fetcher: () => Promise<Blob>) => {
    setStatus("fetching");
    setError(null);

    try {
      const blob = await fetcher();
      const blobURL = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.style.display = "none";
      a.href = blobURL;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(blobURL);
      setStatus("success");
    } catch (err: unknown) {
      setError(err);
      setStatus("error");
    }
  }, []);

  return useMemo(() => ({ download, status, error }), [download, status, error]);
}

export default useFileDownloader;
